import {
  BRIEFING_EMPTY_CHECKLIST,
  BRIEFING_LAND_CHARGE_CERTIFICATE_LABELS,
  BRIEFING_LAND_REGISTER_LABELS,
  BRIEFING_NO_ENTRIES,
  BRIEFING_NOT_MENTIONED,
  BRIEFING_RECOMMENDATION_NOTE,
  BRIEFING_SECTION_TITLES,
  BRIEFING_SUBTITLE,
  BRIEFING_TABLE_HEADINGS,
  BRIEFING_UNIT_NO_FINDINGS,
  type Briefing,
  type BriefingBody,
  briefingBasisText,
  briefingDocumentName,
  briefingEmptyText,
  briefingFinanceLines,
  briefingLandRegisterSource,
  briefingNotMentionedFinding,
  briefingSectionState,
  briefingUnitLine,
  renovationsHeading,
  briefingValue as value,
} from '@acasa/shared'
import type { ReactNode } from 'react'
import { formatDate } from './format'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-7">
      <h3 className="text-brand border-line border-b pb-1 text-sm font-semibold">{title}</h3>
      <div className="mt-3 space-y-2">{children}</div>
    </section>
  )
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="marker:text-faint list-disc space-y-1.5 pl-5">
      {items.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: model text, may repeat; order is fixed
        <li key={index}>{value(item)}</li>
      ))}
    </ul>
  )
}

/** A "zu klären" list: the note that it is advice, then boxes to tick in the conversation. */
function Checklist({ items, empty }: { items: string[]; empty: string }) {
  return (
    <>
      <p className="text-muted text-[11px]">{BRIEFING_RECOMMENDATION_NOTE}</p>
      {items.length > 0 ? (
        <ul className="space-y-1.5">
          {items.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: model text in a fixed order
            <li key={index} className="flex items-start gap-2.5">
              {/* Visual only, as on paper: a box to tick in the conversation. */}
              <span aria-hidden className="border-ink mt-1 size-3 shrink-0 border" />
              <span>{value(item)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p>{empty}</p>
      )}
    </>
  )
}

function Table({
  heads,
  rows,
  alignLast = false,
}: {
  heads: readonly string[]
  rows: string[][]
  alignLast?: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <table className="border-line-strong w-full border-collapse border text-xs">
        <thead>
          <tr className="bg-raised">
            {heads.map((head, index) => (
              <th
                key={head}
                scope="col"
                className={`border-line-strong text-ink border px-2 py-1.5 font-semibold ${
                  alignLast && index === heads.length - 1 ? 'text-right' : 'text-left'
                }`}
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own
            <tr key={rowIndex}>
              {row.map((cell, index) => (
                <td
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed columns
                  key={index}
                  className={`border-line-strong border px-2 py-1.5 align-top ${
                    index === 0 ? 'text-ink font-semibold' : 'text-ink'
                  } ${alignLast && index === row.length - 1 ? 'text-right' : ''}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The briefing as the PDF lays it out, section for section and label for
 * label — the headings and table heads come from `@acasa/shared`, so the page
 * and the document cannot name a section differently. Like the PDF, a section
 * with nothing in it is left out, except "Mit der Verwaltung zu klären"; the
 * sections added in prompt version 2 are printed, stated empty or left out
 * as `briefingSectionState` says — the PDF asks the same function.
 */
export function BriefingView({
  briefing,
  body,
  showTitle = true,
}: {
  briefing: Briefing
  body: BriefingBody
  /** False when the page around it already names the object. */
  showTitle?: boolean
}) {
  const basis = briefingBasisText(body.introduction.basis)
  const finances = briefingFinanceLines(body.finances, briefing.promptVersion)
  const hasRenovations =
    body.renovations.measures.length > 0 || body.renovations.financing.length > 0
  const state = (key: Parameters<typeof briefingSectionState>[1]) =>
    briefingSectionState(body, key, briefing.promptVersion)
  const condition = state('condition')
  const development = state('development')
  const community = state('community')
  const unit = state('unit')
  const landRegister = state('landRegister')
  const seller = state('toClarifySeller')
  const source = briefingLandRegisterSource(body.landRegister.source ?? '')

  return (
    <article className="bg-surface border-line text-ink border px-5 py-6 text-[13px] leading-relaxed sm:px-8">
      <header className="border-brand border-b pb-3">
        {showTitle && (
          <h2 className="font-display text-ink text-xl leading-snug">
            {briefingDocumentName(briefing)}
          </h2>
        )}
        <p className={showTitle ? 'text-muted mt-1 text-sm' : 'text-ink text-sm font-medium'}>
          {BRIEFING_SUBTITLE}
        </p>
        <p className="text-faint mt-1 text-[11px]">
          Stand: {formatDate(briefing.finishedAt)}
          {briefing.estate.externalNumber && ` · Objekt-Nr. ${briefing.estate.externalNumber}`}
        </p>
      </header>

      <div className="mt-4 space-y-2">
        <p>{value(body.introduction.summary)}</p>
        {basis && (
          <p>
            <span className="font-semibold">Grundlage:</span> {basis}
          </p>
        )}
      </div>

      {body.specialQuestions.map((entry, index) => {
        const finding = entry.mentioned ? entry.finding : briefingNotMentionedFinding(entry.finding)
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: questions are in the agent's order
          <Section key={index} title={value(entry.question)}>
            {entry.mentioned ? (
              <p>{value(finding)}</p>
            ) : (
              <p>
                <span className="font-semibold">{BRIEFING_NOT_MENTIONED}</span> {finding}
              </p>
            )}
            {entry.indirectHints.length > 0 && <Bullets items={entry.indirectHints} />}
          </Section>
        )
      })}

      {hasRenovations && (
        <Section title={renovationsHeading(body.renovations.context)}>
          {body.renovations.measures.length > 0 && (
            <Table
              heads={BRIEFING_TABLE_HEADINGS.measures}
              alignLast
              rows={body.renovations.measures.map((row) => [
                value(row.measure),
                value(row.company),
                value(row.grossCost),
              ])}
            />
          )}
          {body.renovations.financing.length > 0 && <Bullets items={body.renovations.financing} />}
        </Section>
      )}

      {condition !== 'omit' && (
        <Section title={BRIEFING_SECTION_TITLES.condition}>
          {condition === 'content' ? (
            <Table
              heads={BRIEFING_TABLE_HEADINGS.condition}
              rows={body.condition.items.map((item) => [
                value(item.measure),
                value(item.year),
                value(item.note),
              ])}
            />
          ) : (
            <p>{briefingEmptyText('condition')}</p>
          )}
        </Section>
      )}

      {body.costRisks.length > 0 && (
        <Section title={BRIEFING_SECTION_TITLES.costRisks}>
          <Table
            heads={BRIEFING_TABLE_HEADINGS.costRisks}
            rows={body.costRisks.map((risk) => [value(risk.topic), value(risk.status)])}
          />
        </Section>
      )}

      {finances && (
        <Section title={BRIEFING_SECTION_TITLES.finances}>
          {finances.map((line) => (
            <p key={line.label}>
              <span className="font-semibold">{line.label}</span> {line.text}
            </p>
          ))}
        </Section>
      )}

      {development === 'content' && (
        <Section title={BRIEFING_SECTION_TITLES.development}>
          <Table
            heads={BRIEFING_TABLE_HEADINGS.development}
            rows={body.development.map((row) => [
              value(row.year),
              value(row.hausgeld),
              value(row.reserve),
              value(row.specialLevy),
            ])}
          />
        </Section>
      )}

      {community !== 'omit' && (
        <Section title={BRIEFING_SECTION_TITLES.community}>
          {community === 'content' ? (
            <Bullets items={body.community} />
          ) : (
            <p>{briefingEmptyText('community')}</p>
          )}
        </Section>
      )}

      {body.administrationAndSale.length > 0 && (
        <Section title={BRIEFING_SECTION_TITLES.administrationAndSale}>
          <Bullets items={body.administrationAndSale} />
        </Section>
      )}

      {unit !== 'omit' && (
        <Section title={BRIEFING_SECTION_TITLES.unit}>
          <p>{briefingUnitLine(body.unit.designation ?? '')}</p>
          {unit === 'content' ? (
            <Bullets items={body.unit.items} />
          ) : (
            <p>{BRIEFING_UNIT_NO_FINDINGS}</p>
          )}
        </Section>
      )}

      {landRegister !== 'omit' && (
        <Section title={BRIEFING_SECTION_TITLES.landRegister}>
          {landRegister === 'content' ? (
            <>
              <p>
                <span className="font-semibold">{source.label}</span> {source.text}
              </p>
              <p className="font-semibold">{BRIEFING_LAND_REGISTER_LABELS.encumbrances}:</p>
              {body.landRegister.encumbrances.length > 0 ? (
                <Table
                  heads={BRIEFING_TABLE_HEADINGS.encumbrances}
                  rows={body.landRegister.encumbrances.map((entry) => [
                    value(entry.kind),
                    value(entry.detail),
                  ])}
                />
              ) : (
                <p>{BRIEFING_NO_ENTRIES}</p>
              )}
              <p className="font-semibold">{BRIEFING_LAND_REGISTER_LABELS.landCharges}:</p>
              {body.landRegister.landCharges.length > 0 ? (
                <Table
                  heads={BRIEFING_TABLE_HEADINGS.landCharges}
                  rows={body.landRegister.landCharges.map((charge) => [
                    value(charge.kind),
                    value(charge.amount),
                    BRIEFING_LAND_CHARGE_CERTIFICATE_LABELS[charge.certificate],
                    value(charge.creditor),
                    value(charge.note),
                  ])}
                />
              ) : (
                <p>{BRIEFING_NO_ENTRIES}</p>
              )}
            </>
          ) : (
            <p>{briefingEmptyText('landRegister')}</p>
          )}
        </Section>
      )}

      <Section title={BRIEFING_SECTION_TITLES.toClarify}>
        <Checklist items={body.toClarify} empty={BRIEFING_EMPTY_CHECKLIST} />
      </Section>

      {seller !== 'omit' && (
        <Section title={BRIEFING_SECTION_TITLES.toClarifySeller}>
          <Checklist items={body.toClarifySeller} empty={briefingEmptyText('toClarifySeller')} />
        </Section>
      )}
    </article>
  )
}
